import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../../context/UserContext';
import { useAlert } from '../../context/AlertContext';
import PasswordInput from '../common/PasswordInput';
import { getFirestore, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { app } from '../../firebase/config';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { setAuthToken } from '../../services/backendCheckout';
import {
  sendEmailOtp,
  resendEmailOtp,
  verifyEmailOtp,
  resetPassword as resetPasswordRequest,
} from '../../services/authService';

const AuthModal = ({ 
  isOpen, 
  onClose, 
  initialMode = 'login', 
  redirectPath = '',
  message = '',
  onSuccess = () => {}
}) => {
  const [mode, setMode] = useState(initialMode); // 'login', 'signup', 'forgotPassword', or 'completeProfile'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Forgot password states
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  // Short-lived proof from the backend that the reset OTP was correct.
  const [resetToken, setResetToken] = useState('');

  // Signup email verification: 'form' collects the details, 'otp' is the verification wall.
  const [signupStep, setSignupStep] = useState('form');
  const [signupOtp, setSignupOtp] = useState('');
  const [signupToken, setSignupToken] = useState('');

  // Google Sign-In completion states
  const [googleUserData, setGoogleUserData] = useState(null);
  
  const { login, register, setCurrentUser } = useUser();
  const { showSuccess, showError } = useAlert();

  // Phone OTP states
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' | 'phone'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneStep, setPhoneStep] = useState('enterPhone'); // 'enterPhone' | 'enterOtp' | 'newUser'
  const [phoneNewUserPhone, setPhoneNewUserPhone] = useState('');
  const [phoneVerificationToken, setPhoneVerificationToken] = useState('');
  const [phoneNewUserForm, setPhoneNewUserForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [phoneNewUserErrors, setPhoneNewUserErrors] = useState({});
  const [resendCooldown, setResendCooldown] = useState(0);
  const [phoneErrors, setPhoneErrors] = useState({});
  const resendTimerRef = useRef(null);

  useEffect(() => {
    if (resendCooldown > 0) {
      resendTimerRef.current = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    }
    return () => clearTimeout(resendTimerRef.current);
  }, [resendCooldown]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Validate email
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Validate password for both login and signup
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (mode === 'signup' && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    // Additional validations for signup mode
    if (mode === 'signup') {
      if (!formData.name) {
        newErrors.name = 'Name is required';
      }
      
      // Validate phone only for signup - make it mandatory with red asterisk
      if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formData.phone)) {
        newErrors.phone = 'Phone number must be 10 digits';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    // Validate for profile completion mode
    if (mode === 'completeProfile') {
      if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formData.phone)) {
        newErrors.phone = 'Phone number must be 10 digits';
      }
      
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (mode === 'completeProfile') {
      return handleCompleteProfile(e);
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);

    try {
      if (mode === 'login') {
        // Attempt login with email and password only
        await login(formData.email, formData.password);
        showSuccess('Successfully logged in!', 'Welcome Back');
        finishAuth();
        return;
      }

      // Signup: don't create anything yet — send a code and make the user prove the address.
      await sendEmailOtp(formData.email, 'signup');
      setSignupOtp('');
      setSignupToken('');
      setSignupStep('otp');
      setResendCooldown(30);
      showSuccess(`Verification code sent to ${formData.email}`, 'Check Your Email');
    } catch (error) {
      console.error(`${mode === 'login' ? 'Login' : 'Registration'} error:`, error);
      showError(
        error.message || `Failed to ${mode === 'login' ? 'login' : 'register'}. Please try again.`,
        mode === 'login' ? 'Login Failed' : 'Verification Failed'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Shared success tail: brief delay so state updates settle before the modal closes.
  const finishAuth = () => {
    setTimeout(() => {
      onSuccess();
      onClose();
      if (redirectPath) {
        window.location.href = redirectPath;
      }
    }, 300);
  };

  // Signup step 2 — verify the emailed code, then create the account with the token the
  // backend hands back.
  const handleSignupOtpVerification = async (e) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(signupOtp)) {
      setErrors({ signupOtp: 'Enter the 6-digit code from your email' });
      return;
    }

    setErrors({});
    setIsLoading(true);

    try {
      // The code is single-use. Hold on to the token it buys so a failed account creation
      // (network blip, etc.) can be retried without asking for a fresh code.
      let verificationToken = signupToken;

      if (!verificationToken) {
        ({ verificationToken } = await verifyEmailOtp(formData.email, signupOtp, 'signup'));
        setSignupToken(verificationToken);
      }

      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        verificationToken
      });

      showSuccess('Account created successfully!', 'Welcome to Petzify');
      finishAuth();
    } catch (error) {
      console.error('Signup verification error:', error);
      setErrors({ signupOtp: error.message });
      setIsLoading(false);
    }
  };

  const handleSignupOtpResend = async () => {
    if (resendCooldown > 0 || isLoading) return;

    setIsLoading(true);
    try {
      await resendEmailOtp(formData.email, 'signup');
      setResendCooldown(30);
      showSuccess('A new verification code is on its way', 'Code Resent');
    } catch (error) {
      if (error.waitSecs) setResendCooldown(error.waitSecs);
      showError(error.message, 'Resend Failed');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForgotPasswordState = () => {
    setForgotPasswordStep(1);
    setForgotPasswordEmail('');
    setOtp('');
    setNewPassword('');
    setConfirmNewPassword('');
    setResetToken('');
  };

  const toggleMode = () => {
    if (mode === 'forgotPassword') {
      setMode('login');
      resetForgotPasswordState();
    } else if (mode === 'completeProfile') {
      setMode('login');
      setGoogleUserData(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
      });
    } else {
      setMode(mode === 'login' ? 'signup' : 'login');
      setSignupStep('form');
      setSignupOtp('');
      setSignupToken('');
    }
    setErrors({});
  };

  // Handle forgot password link click
  const handleForgotPasswordClick = () => {
    setMode('forgotPassword');
    setForgotPasswordStep(1);
    setErrors({});
  };
  
  // Handle forgot password request — the backend generates, emails and stores the code.
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!forgotPasswordEmail.trim() || !/\S+@\S+\.\S+/.test(forgotPasswordEmail)) {
      setErrors({ forgotPasswordEmail: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);

    try {
      await sendEmailOtp(forgotPasswordEmail, 'reset');
      setOtp('');
      setResetToken('');
      setForgotPasswordStep(2);
      setResendCooldown(30);
      showSuccess('Verification code sent to your email', 'Check Your Email');
    } catch (err) {
      console.error('Forgot password error:', err);
      setErrors({ forgotPasswordEmail: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordResend = async () => {
    if (resendCooldown > 0 || isLoading) return;

    setIsLoading(true);
    try {
      await resendEmailOtp(forgotPasswordEmail, 'reset');
      setResendCooldown(30);
      showSuccess('A new verification code is on its way', 'Code Resent');
    } catch (err) {
      if (err.waitSecs) setResendCooldown(err.waitSecs);
      showError(err.message, 'Resend Failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify the reset code. The backend checks it and returns the token that authorises the
  // password change — the code itself is never known to the browser.
  const handleOtpVerification = async (e) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(otp)) {
      setErrors({ otp: 'Enter the 6-digit code from your email' });
      return;
    }

    setIsLoading(true);

    try {
      const { verificationToken } = await verifyEmailOtp(forgotPasswordEmail, otp, 'reset');
      setResetToken(verificationToken);
      setForgotPasswordStep(3);
      setErrors({});
    } catch (err) {
      setErrors({ otp: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset Password
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate passwords
    const newErrors = {};

    if (!newPassword) {
      newErrors.newPassword = 'Please enter a new password';
    } else if (newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (newPassword !== confirmNewPassword) {
      newErrors.confirmNewPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    try {
      await resetPasswordRequest(resetToken, newPassword);

      setMode('login');
      resetForgotPasswordState();

      showSuccess('Password updated successfully. You can now log in with your new password.', 'Password Reset Complete');
    } catch (err) {
      console.error('Password reset error:', err);
      showError(err.message, 'Reset Failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle back button in forgot password flow
  const handleBack = () => {
    if (forgotPasswordStep > 1) {
      setForgotPasswordStep(forgotPasswordStep - 1);
      setOtp('');
    } else {
      setMode('login');
    }
    setErrors({});
  };
  
  // Handle profile completion for Google Sign-In users
  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const db = getFirestore(app);
      
      // Update user document with phone number and password
      const userDoc = {
        ...googleUserData,
        phone: formData.phone,
        password: formData.password,
        profileCompleted: true,
        lastLogin: new Date().toISOString()
      };
      
      await setDoc(doc(db, 'users', googleUserData.email), userDoc);
      
      // Set user in context
      const userContext = {
        id: googleUserData.email,
        email: googleUserData.email,
        name: googleUserData.name,
        phone: formData.phone,
        lastLogin: new Date().toISOString(),
        isAdmin: false
      };
      
      setCurrentUser(userContext);
      localStorage.setItem('user', JSON.stringify(userContext));
      
      showSuccess('Profile completed successfully!', 'Welcome');
      onSuccess();
      onClose();
      
      if (redirectPath) {
        window.location.href = redirectPath;
      }
    } catch (error) {
      console.error('Profile completion error:', error);
      showError('Failed to complete profile. Please try again.', 'Profile Completion Failed');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if user exists in Firestore
      const db = getFirestore(app);
      const userDoc = await getDoc(doc(db, 'users', user.email));
      
      if (userDoc.exists()) {
        // User exists, check if profile is complete
        const userData = userDoc.data();
        
        // Check if phone number is missing
        if (!userData.phone || userData.phone === '') {
          // Show profile completion dialog
          setGoogleUserData({
            name: userData.name || user.displayName,
            email: user.email,
            createdAt: userData.createdAt || new Date().toISOString(),
            admin: userData.admin || false
          });
          setFormData({
            name: userData.name || user.displayName,
            email: user.email,
            phone: '',
            password: '',
            confirmPassword: ''
          });
          setMode('completeProfile');
          setIsLoading(false);
          return;
        }
        
        // Profile is complete, proceed with login
        await updateDoc(doc(db, 'users', user.email), {
          lastLogin: new Date().toISOString()
        });
        
        // Set user in context
        const userContext = {
          id: user.email,
          email: user.email,
          name: userData.name || user.displayName,
          phone: userData.phone || '',
          lastLogin: new Date().toISOString(),
          isAdmin: userData.admin === true
        };
        
        setCurrentUser(userContext);
        localStorage.setItem('user', JSON.stringify(userContext));
      } else {
        // User doesn't exist, show profile completion dialog for new user
        setGoogleUserData({
          name: user.displayName,
          email: user.email,
          createdAt: new Date().toISOString(),
          admin: false
        });
        setFormData({
          name: user.displayName,
          email: user.email,
          phone: '',
          password: '',
          confirmPassword: ''
        });
        setMode('completeProfile');
        setIsLoading(false);
        return;
      }
      
      showSuccess('Successfully signed in with Google!', 'Welcome');
      onSuccess();
      onClose();
      
      if (redirectPath) {
        window.location.href = redirectPath;
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      showError('Failed to sign in with Google. Please try again.', 'Sign In Failed');
    } finally {
      setIsLoading(false);
    }
  };
  
  const API_BASE = 'https://api.petzify.in';

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(phoneNumber)) {
      setPhoneErrors({ phone: 'Enter a valid 10-digit phone number' });
      return;
    }
    setPhoneErrors({});
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneNumber }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send OTP');
      setPhoneStep('enterOtp');
      setPhoneOtp('');
      setResendCooldown(30);
      showSuccess('OTP sent to your WhatsApp number', 'OTP Sent');
    } catch (err) {
      showError(err.message, 'Failed to Send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || isLoading) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneNumber }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.waitSecs) setResendCooldown(data.waitSecs);
        throw new Error(data.error || 'Failed to resend OTP');
      }
      setResendCooldown(30);
      showSuccess('OTP resent to your WhatsApp number', 'OTP Resent');
    } catch (err) {
      showError(err.message, 'Resend Failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(phoneOtp)) {
      setPhoneErrors({ otp: 'Enter a valid 6-digit OTP' });
      return;
    }
    setPhoneErrors({});
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneNumber, otp: phoneOtp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'OTP verification failed');

      if (data.status === 'NEW_USER') {
        setPhoneNewUserPhone(data.phone || phoneNumber);
        // Proof the number was verified — /signup requires it to create the account.
        setPhoneVerificationToken(data.verificationToken || '');
        setPhoneNewUserForm({ name: '', email: '', password: '', confirmPassword: '' });
        setPhoneStep('newUser');
        return;
      }

      const userContext = {
        id: data.user.email,
        email: data.user.email,
        name: data.user.name,
        phone: data.user.phone,
        lastLogin: data.user.lastLogin,
        isAdmin: data.user.admin === true,
      };
      setCurrentUser(userContext);
      localStorage.setItem('user', JSON.stringify(userContext));
      setAuthToken(data.token);
      showSuccess('Successfully logged in!', 'Welcome Back');
      setTimeout(() => {
        onSuccess();
        onClose();
        if (redirectPath) window.location.href = redirectPath;
      }, 300);
    } catch (err) {
      showError(err.message, 'Verification Failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneSignup = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!phoneNewUserForm.name.trim()) newErrors.name = 'Name is required';
    if (!phoneNewUserForm.email.trim() || !/\S+@\S+\.\S+/.test(phoneNewUserForm.email)) newErrors.email = 'Valid email is required';
    if (!phoneNewUserForm.password || phoneNewUserForm.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (phoneNewUserForm.password !== phoneNewUserForm.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (Object.keys(newErrors).length > 0) { setPhoneNewUserErrors(newErrors); return; }

    setPhoneNewUserErrors({});
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: phoneNewUserForm.name.trim(),
          email: phoneNewUserForm.email.trim().toLowerCase(),
          phone: phoneNewUserPhone,
          password: phoneNewUserForm.password,
          verificationToken: phoneVerificationToken,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create account');

      const userContext = {
        id: data.user.email,
        email: data.user.email,
        name: data.user.name,
        phone: data.user.phone,
        lastLogin: data.user.lastLogin,
        isAdmin: data.user.admin === true,
      };
      setCurrentUser(userContext);
      localStorage.setItem('user', JSON.stringify(userContext));
      setAuthToken(data.token);
      showSuccess('Account created successfully!', 'Welcome to Petzify');
      setTimeout(() => {
        onSuccess();
        onClose();
        if (redirectPath) window.location.href = redirectPath;
      }, 300);
    } catch (err) {
      showError(err.message, 'Signup Failed');
    } finally {
      setIsLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto p-4 animate-overlay-in motion-reduce:animate-none">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-modal-in motion-reduce:animate-none">
        {/* Header */}
        <div className="bg-primary text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">
            {mode === 'login' && loginMethod === 'phone' ? (
              phoneStep === 'enterPhone' ? 'Sign In with Phone' :
              phoneStep === 'enterOtp' ? 'Verify OTP' :
              'Complete Sign Up'
            ) : mode === 'login' ? 'Sign In' :
              mode === 'signup' ? (signupStep === 'otp' ? 'Verify Your Email' : 'Create Account') :
              mode === 'completeProfile' ? 'Complete Your Profile' :
              forgotPasswordStep === 1 ? 'Forgot Password' :
              forgotPasswordStep === 2 ? 'Verify Code' :
              'Reset Password'}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 focus:outline-none"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        
        {/* Custom Message */}
        {message && (
          <div className="px-6 pt-4 -mb-2">
            <div className="bg-blue-50 text-blue-700 p-3 rounded-md text-sm">
              {message}
            </div>
          </div>
        )}
        
        {/* Method tabs — only for login mode */}
        {mode === 'login' && (
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => { setLoginMethod('email'); setPhoneErrors({}); }}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                loginMethod === 'email'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Email
            </button>
            <button
              type="button"
              onClick={() => { setLoginMethod('phone'); setPhoneStep('enterPhone'); setPhoneNumber(''); setPhoneOtp(''); setPhoneErrors({}); }}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                loginMethod === 'phone'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Phone
            </button>
          </div>
        )}

        {/* Form */}
        {mode === 'forgotPassword' ? (
          <div className="px-6 py-4">
            {forgotPasswordStep === 1 && (
              <form onSubmit={handleForgotPassword}>
                <p className="text-gray-600 mb-4">
                  Enter your email address and we'll send you a verification code to reset your password.
                </p>
                <div className="mb-4">
                  <label htmlFor="forgotPasswordEmail" className="block text-gray-700 font-medium mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="forgotPasswordEmail"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${
                      errors.forgotPasswordEmail ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your email"
                    disabled={isLoading}
                  />
                  {errors.forgotPasswordEmail && (
                    <p className="mt-1 text-red-500 text-sm">{errors.forgotPasswordEmail}</p>
                  )}
                </div>
                
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="text-primary hover:text-primary-dark"
                    disabled={isLoading}
                  >
                    Back to Login
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 ${
                      isLoading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                    } text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send Code'}
                  </button>
                </div>
              </form>
            )}
            
            {forgotPasswordStep === 2 && (
              <form onSubmit={handleOtpVerification}>
                <p className="text-gray-600 mb-4">
                  We've sent a 6-digit verification code to <strong>{forgotPasswordEmail}</strong>. 
                  Enter the code below to continue.
                </p>
                <div className="mb-4">
                  <label htmlFor="otp" className="block text-gray-700 font-medium mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
                      if (errors.otp) setErrors({});
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light tracking-widest text-center text-lg ${
                      errors.otp ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="• • • • • •"
                    maxLength={6}
                    disabled={isLoading}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                  {errors.otp && (
                    <p className="mt-1 text-red-500 text-sm">{errors.otp}</p>
                  )}
                </div>

                <div className="mb-4 text-center text-sm text-gray-500">
                  Didn't receive it?{' '}
                  {resendCooldown > 0 ? (
                    <span className="text-gray-400">Resend in {resendCooldown}s</span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleForgotPasswordResend}
                      disabled={isLoading}
                      className="text-primary hover:text-primary-dark font-medium"
                    >
                      Resend code
                    </button>
                  )}
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="text-primary hover:text-primary-dark"
                    disabled={isLoading}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 ${
                      isLoading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                    } text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Verifying...' : 'Verify Code'}
                  </button>
                </div>
              </form>
            )}
            
            {forgotPasswordStep === 3 && (
              <form onSubmit={handlePasswordReset}>
                <p className="text-gray-600 mb-4">
                  Create a new password for your account.
                </p>
                <div className="mb-4">
                  <label htmlFor="newPassword" className="block text-gray-700 font-medium mb-2">
                    New Password
                  </label>
                  <PasswordInput
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${
                      errors.newPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter new password"
                    disabled={isLoading}
                  />
                  {errors.newPassword && (
                    <p className="mt-1 text-red-500 text-sm">{errors.newPassword}</p>
                  )}
                </div>
                
                <div className="mb-4">
                  <label htmlFor="confirmNewPassword" className="block text-gray-700 font-medium mb-2">
                    Confirm New Password
                  </label>
                  <PasswordInput
                    id="confirmNewPassword"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${
                      errors.confirmNewPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Confirm new password"
                    disabled={isLoading}
                  />
                  {errors.confirmNewPassword && (
                    <p className="mt-1 text-red-500 text-sm">{errors.confirmNewPassword}</p>
                  )}
                </div>
                
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="text-primary hover:text-primary-dark"
                    disabled={isLoading}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 ${
                      isLoading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                    } text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Updating...' : 'Reset Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : mode === 'login' && loginMethod === 'phone' ? (
          <div className="px-6 py-4">
            {phoneStep === 'enterPhone' && (
              <form onSubmit={handleSendOtp}>
                <p className="text-gray-600 mb-4 text-sm">
                  Enter your 10-digit mobile number. We'll send an OTP via WhatsApp.
                </p>
                <div className="mb-4">
                  <label htmlFor="phoneNumber" className="block text-gray-700 font-medium mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      +91
                    </span>
                    <input
                      type="tel"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhoneNumber(v);
                        if (phoneErrors.phone) setPhoneErrors({});
                      }}
                      className={`flex-1 px-3 py-2 border rounded-r-md focus:outline-none focus:ring focus:ring-primary-light ${
                        phoneErrors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter 10-digit number"
                      disabled={isLoading}
                      inputMode="numeric"
                    />
                  </div>
                  {phoneErrors.phone && (
                    <p className="mt-1 text-red-500 text-sm">{phoneErrors.phone}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-2 ${isLoading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'} text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                >
                  {isLoading ? 'Sending OTP...' : 'Send OTP'}
                </button>
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="text-primary hover:text-primary-dark text-sm"
                  >
                    Don't have an account? Sign up
                  </button>
                </div>
              </form>
            )}

            {phoneStep === 'enterOtp' && (
              <form onSubmit={handleVerifyOtp}>
                <p className="text-gray-600 mb-1 text-sm">
                  OTP sent to <strong>+91 {phoneNumber}</strong> via WhatsApp.
                </p>
                <button
                  type="button"
                  onClick={() => { setPhoneStep('enterPhone'); setPhoneOtp(''); setPhoneErrors({}); clearTimeout(resendTimerRef.current); setResendCooldown(0); }}
                  className="text-primary hover:text-primary-dark text-sm mb-4"
                >
                  Change number
                </button>
                <div className="mb-4">
                  <label htmlFor="phoneOtp" className="block text-gray-700 font-medium mb-2">
                    6-digit OTP <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="phoneOtp"
                    value={phoneOtp}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setPhoneOtp(v);
                      if (phoneErrors.otp) setPhoneErrors({});
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light tracking-widest text-center text-lg ${
                      phoneErrors.otp ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="• • • • • •"
                    maxLength={6}
                    disabled={isLoading}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                  {phoneErrors.otp && (
                    <p className="mt-1 text-red-500 text-sm">{phoneErrors.otp}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-2 ${isLoading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'} text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                >
                  {isLoading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <div className="mt-3 text-center text-sm text-gray-500">
                  Didn't receive it?{' '}
                  {resendCooldown > 0 ? (
                    <span className="text-gray-400">Resend in {resendCooldown}s</span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isLoading}
                      className="text-primary hover:text-primary-dark font-medium"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </form>
            )}

            {phoneStep === 'newUser' && (
              <form onSubmit={handlePhoneSignup}>
                <div className="mb-3 bg-blue-50 text-blue-700 p-3 rounded-md text-sm">
                  <p className="font-medium">Welcome to Petzify!</p>
                  <p>No account found for +91 {phoneNewUserPhone}. Create your account to continue.</p>
                </div>
                <div className="mb-3">
                  <label className="block text-gray-700 font-medium mb-1 text-sm">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={phoneNewUserForm.name}
                    onChange={(e) => setPhoneNewUserForm(p => ({ ...p, name: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${phoneNewUserErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Your full name"
                    disabled={isLoading}
                  />
                  {phoneNewUserErrors.name && <p className="mt-1 text-red-500 text-sm">{phoneNewUserErrors.name}</p>}
                </div>
                <div className="mb-3">
                  <label className="block text-gray-700 font-medium mb-1 text-sm">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={phoneNewUserForm.email}
                    onChange={(e) => setPhoneNewUserForm(p => ({ ...p, email: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${phoneNewUserErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="your@email.com"
                    disabled={isLoading}
                  />
                  {phoneNewUserErrors.email && <p className="mt-1 text-red-500 text-sm">{phoneNewUserErrors.email}</p>}
                </div>
                <div className="mb-3">
                  <label className="block text-gray-700 font-medium mb-1 text-sm">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <PasswordInput
                    value={phoneNewUserForm.password}
                    onChange={(e) => setPhoneNewUserForm(p => ({ ...p, password: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${phoneNewUserErrors.password ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Min. 6 characters"
                    disabled={isLoading}
                  />
                  {phoneNewUserErrors.password && <p className="mt-1 text-red-500 text-sm">{phoneNewUserErrors.password}</p>}
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-1 text-sm">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <PasswordInput
                    value={phoneNewUserForm.confirmPassword}
                    onChange={(e) => setPhoneNewUserForm(p => ({ ...p, confirmPassword: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${phoneNewUserErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Confirm password"
                    disabled={isLoading}
                  />
                  {phoneNewUserErrors.confirmPassword && <p className="mt-1 text-red-500 text-sm">{phoneNewUserErrors.confirmPassword}</p>}
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-2 ${isLoading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'} text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            )}
          </div>
        ) : mode === 'signup' && signupStep === 'otp' ? (
          <form onSubmit={handleSignupOtpVerification} className="px-6 py-4">
            <div className="mb-4 bg-secondary-light/40 text-primary-dark p-3 rounded-md text-sm">
              <p className="font-medium">One last step</p>
              <p>
                We sent a 6-digit code to <strong>{formData.email}</strong>. Enter it below to
                confirm this is your email and finish creating your account.
              </p>
            </div>

            <div className="mb-4">
              <label htmlFor="signupOtp" className="block text-gray-700 font-medium mb-2">
                Verification Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="signupOtp"
                value={signupOtp}
                onChange={(e) => {
                  setSignupOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                  if (errors.signupOtp) setErrors({});
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light tracking-widest text-center text-lg ${
                  errors.signupOtp ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="• • • • • •"
                maxLength={6}
                disabled={isLoading}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
              />
              {errors.signupOtp && (
                <p className="mt-1 text-red-500 text-sm">{errors.signupOtp}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-2 ${
                isLoading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
              } text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
            >
              {isLoading ? 'Verifying...' : 'Verify & Create Account'}
            </button>

            <div className="mt-3 text-center text-sm text-gray-500">
              Didn't receive it?{' '}
              {resendCooldown > 0 ? (
                <span className="text-gray-400">Resend in {resendCooldown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={handleSignupOtpResend}
                  disabled={isLoading}
                  className="text-primary hover:text-primary-dark font-medium"
                >
                  Resend code
                </button>
              )}
            </div>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  setSignupStep('form');
                  setSignupOtp('');
                  setSignupToken('');
                  setErrors({});
                  clearTimeout(resendTimerRef.current);
                  setResendCooldown(0);
                }}
                className="text-primary hover:text-primary-dark text-sm"
                disabled={isLoading}
              >
                Use a different email
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-4">
            {/* Profile completion message */}
            {mode === 'completeProfile' && (
              <div className="mb-4">
                <div className="bg-blue-50 text-blue-700 p-3 rounded-md text-sm">
                  <p className="font-medium">Almost there! 🎉</p>
                  <p>Please complete your profile by adding your phone number and setting a password for your account.</p>
                </div>
              </div>
            )}
            
            {/* Existing form content for login/signup */}
            {(mode === 'signup' || mode === 'completeProfile') && (
              <div className="mb-4">
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your full name"
                    disabled={isLoading || mode === 'completeProfile'}
                  />
                {errors.name && (
                  <p className="mt-1 text-red-500 text-sm">{errors.name}</p>
                )}
              </div>
            )}
            
            <div className="mb-4">
              <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your email"
                disabled={isLoading || mode === 'completeProfile'}
              />
              {errors.email && (
                <p className="mt-1 text-red-500 text-sm">{errors.email}</p>
              )}
            </div>
            
            {/* Phone input for signup and profile completion */}
            {(mode === 'signup' || mode === 'completeProfile') && (
              <div className="mb-4">
                <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your phone number"
                  disabled={isLoading}
                />
                {errors.phone && (
                  <p className="mt-1 text-red-500 text-sm">{errors.phone}</p>
                )}
              </div>
            )}
            
            <div className="mb-4">
              <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your password"
                disabled={isLoading}
              />
              {errors.password && (
                <p className="mt-1 text-red-500 text-sm">{errors.password}</p>
              )}
            </div>
            
            {/* Confirm password field for signup and profile completion modes */}
            {(mode === 'signup' || mode === 'completeProfile') && (
              <div className="mb-4">
                <label htmlFor="confirmPassword" className="block text-gray-700 font-medium mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary-light ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-red-500 text-sm">{errors.confirmPassword}</p>
                )}
              </div>
            )}
            
            {/* Forgot Password Link (only in login mode) */}
            {mode === 'login' && (
              <div className="mb-4 text-right">
                <button
                  type="button"
                  onClick={handleForgotPasswordClick}
                  className="text-sm text-primary hover:text-primary-dark"
                >
                  Forgot your password?
                </button>
              </div>
            )}
            
            <button
              type="submit"
              className={`w-full py-2 ${
                isLoading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
              } text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' :
               mode === 'login' ? 'Sign In' :
               mode === 'completeProfile' ? 'Complete Profile' :
               'Continue'}
            </button>
            
            {/* Add divider and Google Sign-In button after the main button - only for login/signup modes */}
            {(mode === 'login' || mode === 'signup') && (
              <div className="mt-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div>
                
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className="mt-4 w-full flex items-center justify-center gap-2 bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" loading="lazy" />
                  Sign in with Google
                </button>
              </div>
            )}
            
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={toggleMode}
                className="text-primary hover:text-primary-dark"
                disabled={isLoading}
              >
                {mode === 'login' ? 'Don\'t have an account? Sign up' : 
                 mode === 'completeProfile' ? 'Back to login' :
                 'Already have an account? Sign in'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AuthModal; 